import moment from 'moment';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

type PausedDate = {
  date: string;
  platform?: string;
};

type OffDay = {
  id: number;
  name_en: string;
};

type Props = {
  pausedDates: PausedDate[];
  offDays: OffDay[];
  today: string;
  title?: string;
};

type DateRowData = {
  date: string;
  label: string;
  dayName: string;
  isToday: boolean;
  isLocked: boolean;
  isOffDay: boolean;
  platform?: string;
};

export default function PauseDatesSummary({
  pausedDates = [],
  offDays = [],
  today,
  title = "Pause Dates"
}: Props) {
  // If no paused dates, don't render the component
  if (pausedDates.length === 0) {
    return null;
  }

  // Calculate locked date threshold (today + 2 days)
  const todayMoment = moment(today);
  const lockedDateThreshold = todayMoment.clone().add(2, 'days');

  // Create off days lookup map (day ID to day name)
  const offDaysMap = new Map(offDays.map(day => [day.id, day.name_en]));

  // Process paused dates into table rows
  const dateRows: DateRowData[] = pausedDates.map(pausedDate => {
    const dateMoment = moment(pausedDate.date);
    const dayOfWeek = dateMoment.day();
    const dayId = dayOfWeek === 0 ? 1 : dayOfWeek + 1; // Convert to 1-7 format (1=Sunday)
    
    return {
      date: pausedDate.date,
      label: `${dateMoment.format('DD MMM YYYY')} | ${dateMoment.format('ddd')}`,
      dayName: dateMoment.format('ddd'),
      isToday: dateMoment.isSame(todayMoment, 'day'),
      isLocked: dateMoment.isSameOrBefore(lockedDateThreshold),
      isOffDay: offDaysMap.has(dayId),
      platform: pausedDate.platform,
    };
  });

  // Sort dates chronologically
  dateRows.sort((a, b) => moment(a.date).diff(moment(b.date)));

  return (
    <Card sx={{ p: 0, border: 'none', boxShadow: 'none', m: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h6">{title}</Typography>
        <Chip 
          label={`${pausedDates.length} ${pausedDates.length === 1 ? 'date' : 'dates'}`}
          size="small"
          color="warning"
          variant="outlined"
        />
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Platform</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dateRows.map((row) => (
              <TableRow
                key={row.date}
                sx={{
                  '&:nth-of-type(odd)': {
                    backgroundColor: 'action.hover',
                  },
                  ...(row.isToday && {
                    backgroundColor: 'primary.lighter',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  }),
                  ...(row.isLocked && {
                    opacity: 0.7,
                    backgroundColor: 'action.disabledBackground',
                    '&:hover': {
                      backgroundColor: 'action.disabledBackground',
                    },
                  }),
                  ...(row.isOffDay && {
                    fontStyle: 'italic',
                    color: 'text.disabled',
                  }),
                }}
              >
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography 
                      variant="body2" 
                      sx={{
                        fontWeight: row.isToday ? 'bold' : 'normal',
                        color: row.isToday ? 'primary.main' : 'inherit',
                      }}
                    >
                      {row.label}
                    </Typography>
                    {row.isToday && (
                      <Chip
                        label="Today"
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                    {row.isLocked && (
                      <Chip
                        label="Locked"
                        size="small"
                        color="warning"
                        variant="filled"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                    {row.isOffDay && (
                      <Chip
                        label="Off"
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Stack>
                </TableCell>
                
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {row.platform === 'client' && (
                      <Chip
                        label="Client"
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                    {row.platform === 'partners' && (
                      <Chip
                        label="Partners"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pausedDates.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No paused dates found
          </Typography>
        </Box>
      )}
    </Card>
  );
}